import {useCallback, useState} from 'react'

type Callbacks = {
  onError: (error: Error) => void
  onSuccess: () => void
}

const AGENT_SESSION_PROMPT = '@copilot resolve the merge conflicts in this pull request'
const COMMENT_BOX_ID = 'new_comment_field'
const COMMENT_BOX_READY_TIMEOUT_MS = 1500

function findCommentBox(): HTMLTextAreaElement | null {
  const element = document.getElementById(COMMENT_BOX_ID)

  if (element instanceof HTMLTextAreaElement) {
    return element
  }

  return null
}

function prefillCommentBox(commentBox: HTMLTextAreaElement) {
  const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set

  if (setter) {
    setter.call(commentBox, AGENT_SESSION_PROMPT)
  } else {
    commentBox.value = AGENT_SESSION_PROMPT
  }

  commentBox.dispatchEvent(new Event('input', {bubbles: true}))
  commentBox.dispatchEvent(new Event('change', {bubbles: true}))
}

function waitForCommentBox(): Promise<HTMLTextAreaElement> {
  const commentBox = findCommentBox()
  if (commentBox) {
    return Promise.resolve(commentBox)
  }

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      observer.disconnect()
      reject(new Error('Failed to find comment box'))
    }, COMMENT_BOX_READY_TIMEOUT_MS)

    const observer = new MutationObserver(() => {
      const nextCommentBox = findCommentBox()
      if (!nextCommentBox) return

      window.clearTimeout(timeout)
      observer.disconnect()
      resolve(nextCommentBox)
    })

    observer.observe(document.body, {childList: true, subtree: true})
  })
}

async function prefillAndScrollToCommentBox() {
  if (typeof window === 'undefined') return

  const commentBox = await waitForCommentBox()
  prefillCommentBox(commentBox)
  commentBox.scrollIntoView({block: 'center'})
  commentBox.focus()
}

export function useAgentConflictResolutionComment({onError, onSuccess}: Callbacks) {
  const [isAgentConflictResolutionCommentPending, setIsAgentConflictResolutionCommentPending] = useState(false)

  const startAgentConflictResolutionComment = useCallback(async () => {
    setIsAgentConflictResolutionCommentPending(true)

    try {
      await prefillAndScrollToCommentBox()
      onSuccess()
    } catch (error) {
      onError(error instanceof Error ? error : new Error('Failed to start agent session'))
    } finally {
      setIsAgentConflictResolutionCommentPending(false)
    }
  }, [onError, onSuccess])

  return {startAgentConflictResolutionComment, isAgentConflictResolutionCommentPending}
}
